/**
 * Location Calendar Component
 *
 * Reusable calendar for selecting location stop dates.
 * Displays available dates with inventory status indicators.
 *
 * @element peach-location-calendar
 *
 * @fires date-selected - Emitted when user selects a calendar date (detail: { date: string, stops: Stop[] })
 * @fires month-changed - Emitted when user navigates to different month
 *
 * @example
 * ```html
 * <peach-location-calendar
 *   .stops=${locationStops}
 *   .selectedStop=${currentStop}
 * ></peach-location-calendar>
 * ```
 */

import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import type { Stop } from "@/types/location";
import { cn } from "@/utils/cn";
import { getStopStatusDisplay } from "@/utils/stop-status";
import "@/features/ui/button";
import "@/features/ui/icon";

@customElement("peach-location-calendar")
export class LocationCalendarComponent extends LitElement {
  /**
   * Render in light DOM to enable Tailwind CSS classes
   */
  createRenderRoot() {
    return this;
  }

  /**
   * Array of stops to display in calendar
   */
  @property({ type: Array })
  declare stops: Stop[];

  /**
   * Currently selected stop
   */
  @property({ type: Object })
  declare selectedStop: Stop | null;

  /**
   * Initial month to display (defaults to first stop's month)
   */
  @property({ type: Object })
  declare initialMonth: Date | undefined;

  /**
   * Calendar state: current month being displayed
   */
  @state()
  declare private currentMonth: Date;

  constructor() {
    super();
    this.stops = [];
    this.selectedStop = null;
    this.currentMonth = new Date();
  }

  /**
   * Watch for property changes
   */
  updated(changedProperties: Map<string, any>) {
    // Navigate to selected stop's month when it changes
    if (changedProperties.has("selectedStop") && this.selectedStop) {
      const selectedDate = new Date(this.selectedStop.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      // If selected stop is in the past, show current month instead
      const targetDate = selectedDate >= today ? selectedDate : today;

      this.currentMonth = new Date(
        targetDate.getFullYear(),
        targetDate.getMonth(),
        1,
      );
    }

    // Use initialMonth if provided
    if (changedProperties.has("initialMonth") && this.initialMonth) {
      this.currentMonth = new Date(
        this.initialMonth.getFullYear(),
        this.initialMonth.getMonth(),
        1,
      );
    }
  }

  /**
   * Navigate to previous month
   */
  private handlePrevMonth() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() - 1,
      1,
    );

    this.dispatchEvent(
      new CustomEvent("month-changed", {
        detail: { month: this.currentMonth },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Navigate to next month
   */
  private handleNextMonth() {
    this.currentMonth = new Date(
      this.currentMonth.getFullYear(),
      this.currentMonth.getMonth() + 1,
      1,
    );

    this.dispatchEvent(
      new CustomEvent("month-changed", {
        detail: { month: this.currentMonth },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Select a date from the calendar
   */
  private handleDateClick(stop: Stop) {
    // Check if date is in the past
    const stopDate = new Date(stop.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const status = getStopStatusDisplay(stop);
    if (stopDate < today || !status.isAvailable) {
      return; // Don't select past dates or unavailable dates
    }

    // Find all stops for this date
    const stopsForDate = this.stops.filter((s) => s.date === stop.date);

    // Emit the date and all stops for that date
    this.dispatchEvent(
      new CustomEvent("date-selected", {
        detail: {
          date: stop.date,
          stops: stopsForDate
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Get calendar dates for current month
   */
  private getCalendarDates(): Date[] {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();

    // First day of month
    const firstDay = new Date(year, month, 1);
    // Last day of month
    const lastDay = new Date(year, month + 1, 0);

    // Start from Sunday before first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on Saturday after last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const dates: Date[] = [];
    const current = new Date(startDate);

    while (current <= endDate) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }

  /**
   * Check if date has a stop
   */
  private getStopForDate(date: Date): Stop | null {
    // Convert Date to MM/DD/YYYY format to match API data
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const dateStr = `${month}/${day}/${year}`;

    return this.stops.find((s) => s.date === dateStr) ?? null;
  }

  /**
   * Check if date is today
   */
  private isToday(date: Date): boolean {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  }

  /**
   * Check if date is in current month
   */
  private isCurrentMonth(date: Date): boolean {
    return date.getMonth() === this.currentMonth.getMonth();
  }

  /**
   * Check if date is selected
   */
  private isDateSelected(date: Date): boolean {
    if (!this.selectedStop) return false;

    // Convert Date to MM/DD/YYYY format to match API data
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const dateStr = `${month}/${day}/${year}`;

    return this.selectedStop.date === dateStr;
  }

  /**
   * Main render method
   */
  render() {
    const dates = this.getCalendarDates();
    const monthName = this.currentMonth.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    return html`
      <div class="bg-cream border-b border-default px-3 py-2">
        <!-- Month Navigation -->
        <div class="flex items-center justify-between mb-2">
          <peach-button variant="ghost-rounded" size="icon-sm" @click=${this.handlePrevMonth}>
            <peach-icon name="chevron-left"></peach-icon>
          </peach-button>
          <h3 class="text-default">${monthName}</h3>
          <peach-button variant="ghost-rounded" size="icon-sm" @click=${this.handleNextMonth}>
            <peach-icon name="chevron-right"></peach-icon>
          </peach-button>
        </div>

        <!-- Calendar Grid -->
        <div class="grid grid-cols-7 gap-0.5">
          <!-- Week Day Headers -->
          ${weekDays.map(
            (day) => html`
              <div class="text-center text-[10px] font-medium text-subtle py-0.5">
                ${day}
              </div>
            `,
          )}

          <!-- Calendar Dates -->
          ${dates.map((date) => {
            const stop = this.getStopForDate(date);
            const isToday = this.isToday(date);
            const isCurrentMonth = this.isCurrentMonth(date);
            const isSelected = this.isDateSelected(date);
            const isPast = date < new Date(new Date().setHours(0, 0, 0, 0));
            const stopStatus = stop ? getStopStatusDisplay(stop) : null;
            const isSoldOutNoWalkUp = stopStatus && !stopStatus.isAvailable;
            const isSoldOutWithWalkUp = stopStatus?.checkoutDisabled && stopStatus.isAvailable;
            const isLimited = stopStatus && stopStatus.text !== "Available" && !stopStatus.checkoutDisabled && stopStatus.text !== "Walk-ups available while supplies last";
            const isAvailable = stop && !isSoldOutNoWalkUp && !isPast;

            // Determine date state for styling
            const isAvailableLimited = isAvailable && !isSelected && (isLimited || isSoldOutWithWalkUp);
            const isAvailableNormal = isAvailable && !isSelected && !isLimited && !isSoldOutWithWalkUp;
            const isUnavailableDate =
              !isSelected &&
              !isAvailable &&
              isCurrentMonth &&
              !isPast &&
              !isSoldOutNoWalkUp;

            const buttonClass = cn(
              "w-full h-7 p-0.5 text-xs rounded transition-colors flex items-center justify-center",
              {
                // Selected date
                "bg-brand-accent text-white font-semibold": isSelected,

                // Available dates
                "bg-warning-soft text-warning-text hover:bg-warning-soft border-warning-ring cursor-pointer":
                  isAvailableLimited,
                "bg-success-soft text-success-text hover:bg-success-soft border-success-ring cursor-pointer":
                  isAvailableNormal,

                // Unavailable dates (sold out without walkup)
                "bg-error-soft text-error-icon border-error-ring line-through cursor-not-allowed":
                  isSoldOutNoWalkUp && !isSelected,
                "text-subtle cursor-not-allowed": isPast && !isSoldOutNoWalkUp && !isSelected,
                "text-subtle opacity-50": !isCurrentMonth,
                "text-muted": isUnavailableDate,

              },
            );

            // Today indicator: small circle around just the number
            const numberClass = isToday
              ? "w-5 h-5 flex items-center justify-center rounded-full bg-gold text-default"
              : "";

            return html`
              <button
                @click=${() => stop && isAvailable && this.handleDateClick(stop)}
                class="${buttonClass}"
                ?disabled=${!isAvailable}
                aria-label="${date.toLocaleDateString()}"
              >
                <span class="${numberClass}">${date.getDate()}</span>
              </button>
            `;
          })}
        </div>

        <!-- Legend -->
        <div class="flex flex-wrap gap-x-3 gap-y-1 mt-2 pt-2 border-t border-default text-[10px] text-subtle">
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 rounded bg-success-soft border-success-ring"></span>
            <span>Available</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 rounded bg-warning-soft border-warning-ring"></span>
            <span>Limited</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 rounded bg-error-soft border-error-ring"></span>
            <span>Sold Out</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 rounded bg-brand-accent"></span>
            <span>Selected</span>
          </div>
          <div class="flex items-center gap-1">
            <span class="w-3 h-3 rounded-full bg-gold"></span>
            <span>Today</span>
          </div>
        </div>

      </div>
    `;
  }
}

/**
 * TypeScript declaration for JSX/Liquid integration
 */
declare global {
  interface HTMLElementTagNameMap {
    "peach-location-calendar": LocationCalendarComponent;
  }
}
